// 경량 HTML sanitizer (DOM 비의존, web/api 공용).
// 공지 본문(Rich Text)은 Admin 만 작성하므로 신뢰도가 높으나, 방어적으로 위험 구문을 제거한다.
// 허용: 서식 태그(b,i,u,s,strong,em,p,br,ul,ol,li,h1~h3,blockquote,a,span,div,code,pre).
// 제거: script/style/iframe 등 위험 태그, on* 이벤트 핸들러, javascript: URL.
const DANGEROUS_TAGS = /<\s*(script|style|iframe|object|embed|link|meta|form|input|button|svg|math)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
const DANGEROUS_SELF = /<\s*(script|style|iframe|object|embed|link|meta|form|input|button|svg|math)\b[^>]*\/?>/gi;
const EVENT_ATTR = /\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_URL = /((?:href|src)\s*=\s*)("|')?\s*javascript:[^"'>\s]*/gi;
export function sanitizeHtml(input) {
    if (!input)
        return '';
    let s = String(input);
    s = s.replace(DANGEROUS_TAGS, '');
    s = s.replace(DANGEROUS_SELF, '');
    s = s.replace(EVENT_ATTR, '');
    s = s.replace(JS_URL, '$1"#"');
    return s;
}
/** HTML → 순수 텍스트(목록 미리보기용). 태그 제거 + 공백 정리. */
export function htmlToText(input) {
    return String(input ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim();
}
// ── 허용목록 방식(주간보고 등 전 임직원이 작성하고 전원이 보는 서식 텍스트) ──
// 정규식 블랙리스트(sanitizeHtml)는 <img/onerror=..> · &#106;avascript: 같은 변형을 놓칠 수 있어,
// 여기서는 허용 태그만 '속성 없이' 다시 쓰고(a 는 http(s)/mailto href 만), 나머지 '<' 는 모두 문자로 이스케이프한다.
const RICH_TAGS = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'p', 'br', 'div', 'span', 'ul', 'ol', 'li', 'blockquote']);
const SAFE_HREF = /^(https?:\/\/|mailto:)[^\s"'<>&]*$/i;
const attr = (attrs, name) => {
    const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(attrs);
    return (m?.[1] ?? m?.[2] ?? m?.[3] ?? '').trim();
};
// 글자 색·크기만 허용(값도 형식 고정) — 그 외 style(배경 이미지·위치 등)은 버림
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\))$/i;
const SAFE_SIZE = /^(x-small|small|medium|large|x-large|xx-large|xxx-large|\d{1,2}(\.\d)?px)$/i;
const FONT_SIZE_KEYWORDS = ['', 'x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'xxx-large']; // <font size=1~7>
// 붙여넣은 이미지는 data URI(인증 헤더가 필요한 API URL 은 <img> 로 못 불러옴) — 래스터 형식만
const SAFE_IMG = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i;
function safeStyle(color, size) {
    const parts = [];
    if (SAFE_COLOR.test(color))
        parts.push(`color:${color}`);
    if (SAFE_SIZE.test(size))
        parts.push(`font-size:${size}`);
    return parts.length ? ` style="${parts.join(';')}"` : '';
}
export function sanitizeRichText(input) {
    if (!input)
        return '';
    return String(input)
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(DANGEROUS_TAGS, '')
        .replace(/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^<>]*)>|</g, (_m, close, tag, attrs = '') => {
        if (!tag)
            return '&lt;'; // 태그가 아닌 '<'
        const t = tag.toLowerCase();
        if (t === 'a') {
            if (close)
                return '</a>';
            const url = attr(attrs, 'href');
            return SAFE_HREF.test(url) ? `<a href="${url}" target="_blank" rel="noopener noreferrer">` : '<a>';
        }
        if (t === 'img') {
            const src = attr(attrs, 'src');
            return !close && SAFE_IMG.test(src) ? `<img src="${src.replace(/\s/g, '')}" alt="">` : '';
        }
        // span style / font color·size → <span style="color;font-size"> 로 정규화
        if (t === 'span' || t === 'font') {
            if (close)
                return '</span>';
            const style = attr(attrs, 'style');
            const pick = (k) => new RegExp(`(?:^|;)\\s*${k}\\s*:\\s*([^;]+)`, 'i').exec(style)?.[1]?.trim() ?? '';
            const color = t === 'font' ? attr(attrs, 'color') : pick('color');
            const size = t === 'font' ? FONT_SIZE_KEYWORDS[Number(attr(attrs, 'size'))] ?? '' : pick('font-size');
            return `<span${safeStyle(color, size)}>`;
        }
        if (!RICH_TAGS.has(t))
            return '';
        return t === 'br' ? '<br>' : `<${close ? '/' : ''}${t}>`;
    });
}
/** 예전 순수 텍스트(줄바꿈)도 서식 텍스트로 표시 — 태그가 없으면 이스케이프 + 줄바꿈→<br> */
export function toRichHtml(s) {
    if (!s)
        return '';
    if (/<[a-z][^>]*>/i.test(s))
        return sanitizeRichText(s);
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}
/** 내용이 비었는지(태그·공백만) */
export const isBlankRich = (s) => htmlToText(s ?? '') === '';
//# sourceMappingURL=sanitize.js.map